/**
 * Professional profile (Bucket C) — /professionals/:id.
 *
 * One unified profile for any directory professional (individual or company):
 * header + identity, a badge per VERIFIED licence the owner holds, a
 * self-description (bio) + specialties + service areas, contact actions, and
 * their latest listings (fetched via the existing /api/properties?companyId= or
 * /api/agents/:id/properties endpoints, so the card shape is the proven one).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import ResultCard from '../search/components/ResultCard';
import { toFrontendProperty, type SearchProperty } from '../../services/ragSearchService';
import type { Property } from '../../mocks/properties';
import {
  fetchProfessionalProfile,
  directoryTypeDef,
  badgeLabel,
  type ProfessionalProfileData,
} from '../../services/professionalsService';
import { apiRequest } from '../../utils/apiClient';

export default function ProfessionalProfilePage() {
  const { i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ProfessionalProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [listings, setListings] = useState<Property[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    fetchProfessionalProfile(id)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  // Latest listings via the existing endpoints — company by companyId, individual
  // broker by agentId. Other individual roles have no listings (no Agent) → skip.
  useEffect(() => {
    const owner = data?.owner;
    if (!owner) return;
    const path = owner.companyId
      ? `/properties?companyId=${owner.companyId}&page=1&pageSize=6`
      : owner.agentId
        ? `/agents/${owner.agentId}/properties?page=1&pageSize=6`
        : null;
    if (!path) { setListings([]); return; }
    setListingsLoading(true);
    apiRequest<{ data: SearchProperty[] }>(path)
      .then((res) => setListings((res.data ?? []).map(toFrontendProperty) as Property[]))
      .catch(() => setListings([]))
      .finally(() => setListingsLoading(false));
  }, [data?.owner]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="mx-auto max-w-xl px-4 py-24 text-center">
          <h1 className="mb-4 text-2xl font-bold text-[var(--pv-color-primary)]">{isRTL ? 'المختصّ غير موجود' : 'Professional not found'}</h1>
          <Link to="/professionals"><Button variant="primary">{isRTL ? 'تصفّح الدليل' : 'Browse the directory'}</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { owner, profile, ownerType, activity, badges } = data;
  const isCompany = ownerType === 'company';
  const name = owner.name || (isRTL ? 'مختصّ عقاري' : 'Professional');
  const def = directoryTypeDef(activity.activityType);
  const roleLabel = def ? (isRTL ? def.singularAr : def.singularEn) : activity.activityType;
  const city = profile?.city || null;
  const waNumber = (profile?.whatsapp || profile?.businessPhone || '').replace(/[^0-9]/g, '');

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main id="main-content" className="pb-20">
        <div className="mx-auto max-w-6xl px-4 py-6">
          {/* Header card */}
          <section className="mb-4 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
            <div className="h-24 bg-[#004D40]" />
            <div className="px-5 pb-6 sm:px-8">
              <div className={`-mt-12 flex flex-col gap-4 sm:flex-row sm:items-end ${isRTL ? 'sm:flex-row-reverse sm:text-right' : ''}`}>
                <div className="shrink-0">
                  {owner.avatarUrl ? (
                    <img src={owner.avatarUrl} alt={name} className={`h-24 w-24 sm:h-28 sm:w-28 ring-4 ring-white shadow-md object-cover bg-white ${isCompany ? 'rounded-2xl' : 'rounded-full'}`} />
                  ) : (
                    <div className={`flex h-24 w-24 items-center justify-center bg-[#004D40] ring-4 ring-white shadow-md sm:h-28 sm:w-28 ${isCompany ? 'rounded-2xl' : 'rounded-full'}`}>
                      <span className="text-3xl font-bold text-white">{name.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1 pt-2 sm:pt-0">
                  <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h1 className="truncate font-display text-xl font-bold text-[var(--pv-color-primary)] sm:text-2xl">{name}</h1>
                    <i className="ri-verified-badge-fill text-xl text-[#004D40]" title={isRTL ? 'موثّق' : 'Verified'} aria-hidden="true" />
                  </div>
                  <div className={`mt-2.5 flex flex-wrap items-center gap-3 text-xs text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pv-color-surface-sunken)] px-2.5 py-1 font-semibold text-[#003930]">
                      {def && <i className={def.icon} aria-hidden="true" />}{roleLabel}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ${isCompany ? 'bg-teal-50 text-teal-700' : 'bg-blue-50 text-blue-700'}`}>
                      <i className={isCompany ? 'ri-building-line' : 'ri-user-line'} aria-hidden="true" />{isCompany ? (isRTL ? 'شركة' : 'Company') : (isRTL ? 'فرد' : 'Individual')}
                    </span>
                    {profile && profile.avgRating > 0 && (
                      <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400" /><span className="font-semibold text-slate-700">{profile.avgRating.toFixed(1)}</span>{profile.totalReviews > 0 && <span>({profile.totalReviews})</span>}</span>
                    )}
                    {city && <span className="flex items-center gap-1"><i className="ri-map-pin-2-line" />{city}</span>}
                    {profile?.yearsExperience ? <span className="flex items-center gap-1"><i className="ri-briefcase-line" />{isRTL ? `${profile.yearsExperience} سنوات خبرة` : `${profile.yearsExperience} yrs experience`}</span> : null}
                  </div>
                </div>

                {/* Contact actions */}
                <div className={`flex shrink-0 items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {waNumber && (
                    <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#004D40] px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90">
                      <i className="ri-whatsapp-line" /><span className="hidden sm:inline">{isRTL ? 'واتساب' : 'WhatsApp'}</span>
                    </a>
                  )}
                  {profile?.businessPhone && (
                    <a href={`tel:${profile.businessPhone}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                      <i className="ri-phone-line" /><span className="hidden sm:inline">{isRTL ? 'اتصال' : 'Call'}</span>
                    </a>
                  )}
                  {profile?.website && (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                      <i className="ri-global-line" /><span className="hidden sm:inline">{isRTL ? 'الموقع' : 'Website'}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Licences / badges — one per VERIFIED activity */}
          {badges.length > 0 && (
            <section className="mb-4 rounded-2xl border border-slate-200/80 bg-white p-5">
              <h2 className={`mb-3 text-sm font-semibold text-slate-700 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'التراخيص الموثّقة' : 'Verified licences'}</h2>
              <div className={`flex flex-wrap gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {badges.map((b) => (
                  <span key={b.activityType} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] px-3 py-1.5 text-xs font-semibold text-[#003930]">
                    <i className="ri-verified-badge-fill" aria-hidden="true" />
                    {badgeLabel(b.activityType, ownerType, isRTL)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* About / bio */}
          {profile?.bio && (
            <section className="mb-4 rounded-2xl border border-slate-200/80 bg-white p-5">
              <h2 className={`mb-2 text-sm font-semibold text-slate-700 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'نبذة' : 'About'}</h2>
              <p className={`text-sm leading-relaxed text-slate-600 ${isRTL ? 'text-right' : ''}`}>{profile.bio}</p>
            </section>
          )}

          {/* Specialties + service areas */}
          {profile && (profile.specialties.length > 0 || profile.serviceAreas.length > 0) && (
            <section className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {profile.specialties.length > 0 && (
                <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
                  <h2 className={`mb-2 text-sm font-semibold text-slate-700 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'التخصّصات' : 'Specialties'}</h2>
                  <div className={`flex flex-wrap gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {profile.specialties.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{s}</span>)}
                  </div>
                </div>
              )}
              {profile.serviceAreas.length > 0 && (
                <div className="rounded-2xl border border-slate-200/80 bg-white p-5">
                  <h2 className={`mb-2 text-sm font-semibold text-slate-700 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'مناطق الخدمة' : 'Service areas'}</h2>
                  <div className={`flex flex-wrap gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {profile.serviceAreas.map((a) => <span key={a} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{a}</span>)}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Latest listings (companies + individual brokers) */}
          {(owner.companyId || owner.agentId) && (
            <section>
              <h2 className={`mb-4 text-xl font-bold text-[var(--pv-color-primary)] ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'أحدث العقارات' : 'Latest listings'}</h2>
              {listingsLoading ? (
                <div className="flex justify-center py-12"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
              ) : listings.length === 0 ? (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 py-12 text-center text-gray-500">{isRTL ? 'لا توجد عقارات نشطة' : 'No active listings'}</div>
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {listings.map((p, index) => (
                    <ResultCard key={p.id} property={p} eagerImage={index < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
